<script setup lang="ts">
  // POS V2 table detail page (ported from -pos pages/pos/table/[id].vue).
  // Replaces ModalPosTable: order list with selection, bulk actions footer and
  // a table nav rail. Data is live from the socket-fed ordersStore.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import { OrderMode, OrderType } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { IOrder } from '@/interfaces/IOrder';
  import { orderToListItem } from '@/domain/pos/order-formatting';
  import type { IOrderListItem } from '@/domain/pos/pos-types';
  import { orderMatchesSearch } from '@/utils/orders.utils';
  import TablesService from '@/api/services/tables.service';
  import { useOrders } from '@/composition/use-orders';
  import { usePosNavigation } from '@/composition/use-pos-navigation';
  import { usePosV2 } from '@/composition/use-pos-v2';
  import { useUi } from '@/composition/use-ui';
  import { useOrdersStore } from '@/stores/orders.store';
  import {
    isEmptyUneditedDraft,
    usePosDraftsStore,
  } from '@/stores/pos-drafts.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { usePosReservationTables } from '@/composition/reservations/use-pos-reservation-tables';
  import V2ReservationTableBanner from '@/components/pos-v2/table/V2ReservationTableBanner.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosV2TableFooter from '@/components/pos-v2/table/PosV2TableFooter.vue';
  import PosV2TableHeader from '@/components/pos-v2/table/PosV2TableHeader.vue';
  import PosV2TableNav from '@/components/pos-v2/table/PosV2TableNav.vue';
  import PosV2TableOrdersList from '@/components/pos-v2/table/PosV2TableOrdersList.vue';

  const { isPosV2Active } = usePosV2();
  const restaurantStore = useRestaurantStore();
  const ordersStore = useOrdersStore();
  const posDrafts = usePosDraftsStore();
  const posNavigation = usePosNavigation();
  const ordersHelper = useOrders();
  const ui = useUi();
  const route = useRoute();
  const router = useRouter();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const posPath = computed(() => `/${restaurantStore.restaurant.stringId}/pos`);

  // Route guard: this page only exists while POS V2 is active.
  watch(
    isPosV2Active,
    (active) => {
      if (!active) router.replace(posPath.value);
    },
    { immediate: true }
  );

  //#region Table + orders data

  const tableId = computed(() => Number(route.params.tableId));

  const table = computed(
    () => restaurantStore.tables?.find((t) => t.id === tableId.value) ?? null
  );

  const isLoading = computed(
    () => !restaurantStore.tables || !ordersStore.pendingOrdersByType
  );

  const tableOrders = computed<IOrder[]>(() => {
    const seatOrders = ordersStore.pendingOrdersByType?.[OrderType.Seat] ?? [];
    return seatOrders.filter((o) => o.tableId === tableId.value);
  });

  const search = ref('');
  const { tables: reservationTables } = usePosReservationTables();
  const reservationHint = computed(
    () =>
      reservationTables.value?.find((value) => value.id === tableId.value)
        ?.reservation
  );

  const filteredOrders = computed<IOrder[]>(() => {
    const q = search.value.trim();
    if (!q) return tableOrders.value;
    return tableOrders.value.filter((order) => orderMatchesSearch(order, q));
  });

  const listItems = computed<IOrderListItem[]>(() =>
    filteredOrders.value.map(orderToListItem)
  );

  //#endregion

  //#region Selection

  const selectedIds = ref<Set<number>>(new Set());

  // Prune selections for orders that left the table (archived, moved, etc).
  watch(tableOrders, (orders) => {
    const liveIds = new Set(orders.map((o) => o.id));
    const pruned = [...selectedIds.value].filter((id) => liveIds.has(id));
    if (pruned.length !== selectedIds.value.size) {
      selectedIds.value = new Set(pruned);
    }
  });

  const selectedOrders = computed<IOrder[]>(() =>
    tableOrders.value.filter((o) => selectedIds.value.has(o.id))
  );

  function toggleSelect(item: IOrderListItem) {
    const next = new Set(selectedIds.value);
    if (next.has(item.id)) next.delete(item.id);
    else next.add(item.id);
    selectedIds.value = next;
  }

  function selectAll() {
    selectedIds.value = new Set(filteredOrders.value.map((o) => o.id));
  }

  function clearSelection() {
    selectedIds.value = new Set();
  }

  //#endregion

  //#region Actions

  function openOrder(item: IOrderListItem) {
    const order = tableOrders.value.find((o) => o.id === item.id);
    if (order) ordersHelper.openOrder(order);
  }

  const isWhatsAppMode = computed(
    () => restaurantStore.selectedBranch?.ordersMode === OrderMode.WhatsApp
  );

  const canCreateOrder = computed(() => !isWhatsAppMode.value && !!table.value);

  function newOrder() {
    createTableOrder(tableId.value);
  }

  function createTableOrder(targetTableId: number) {
    const targetTable = restaurantStore.tables?.find(
      (value) => value.id === targetTableId
    );
    if (!targetTable) return;

    const branch = restaurantStore.selectedBranch;
    if (branch?.allowOrders === false) {
      ui.showToast(AlertType.Info, i18nT('posV2.branchNotAcceptingOrders'));
      return;
    }
    if (branch?.tableOrders === false) {
      ui.showToast(AlertType.Info, i18nT('posV2.flowDisabled'));
      return;
    }

    // Prefer reusing an empty, unedited "create" tab so we don't spam tabs
    // (same behavior as PosV2Header.navigateToNewOrder).
    const active = posDrafts.activeDraft;
    let draftId: string;
    if (isEmptyUneditedDraft(active)) {
      draftId = active.id;
      posDrafts.setActiveDraft(draftId);
      posDrafts.updateDraft(draftId, {
        type: OrderType.Seat,
        tableId: targetTable.id,
        isDirty: false,
      });
    } else {
      draftId = posDrafts.createDraft(OrderType.Seat);
      // Prefill the table without marking the fresh draft dirty, so an
      // untouched tab can still be closed/reused freely.
      posDrafts.updateDraft(draftId, {
        tableId: targetTable.id,
        isDirty: false,
      });
    }

    posNavigation.rememberOrderWorkspaceOrigin(
      targetTableId === tableId.value
        ? route.fullPath
        : `${posPath.value}/table/${targetTableId}`
    );
    router.push(`${posPath.value}/order`);
  }

  // Mirrors ModalPosTableHeader.archiveTable (same service call + recovery).
  const closing = ref(false);
  async function closeTable() {
    if (!table.value || closing.value) return;

    try {
      closing.value = true;
      const archivedOrders = await TablesService.close(table.value.id);
      // Deterministic local cleanup: don't wait for our own WS echo. Removing
      // the archived orders from the store frees the table in the grid
      // immediately and triggers the drafts-store tab prune. Idempotent vs
      // the WS echo and the periodic reload (removeOrder no-ops on missing
      // ids; a late orderUpdate for a non-pending order is ignored by the
      // store).
      for (const order of archivedOrders) {
        ordersStore.removeOrder(order.id);
      }
      ui.showToast(AlertType.Success, i18nT('posV2.tableClosedToast'));
      clearSelection();
    } catch (e) {
      ui.serverError(e, i18nT('posV2.tableCloseError'));

      // Refresh the orders in case they were updated serverside.
      for (const order of tableOrders.value) {
        ordersHelper.refreshOrder({ order }).catch((err) => {
          console.error(
            `Error refreshing order ${order.id} after table archive error`,
            err
          );
        });
      }
    } finally {
      closing.value = false;
    }
  }

  function goBack() {
    router.push(posPath.value);
  }

  function selectTable(id: number) {
    if (id === tableId.value) return;
    clearSelection();
    search.value = '';
    router.replace(`${posPath.value}/table/${id}`);
  }

  //#endregion
</script>

<template>
  <div class="pos-v2-table-page">
    <PosV2TableNav
      :current-table-id="tableId"
      class="page-nav"
      @select="selectTable"
    />

    <div class="page-main">
      <!-- Unknown table (deleted or bad deep link) -->
      <PosUiEmptyState
        v-if="!isLoading && !table"
        icon="table-furniture"
        :title="$t('posV2.tableNotFound')"
        class="not-found-state"
      >
        <template #action>
          <PosUiButton
            color="neutral"
            variant="outline"
            size="lg"
            icon="arrow-left"
            @click="goBack"
          >
            {{ $t('posV2.backToPos') }}
          </PosUiButton>
        </template>
      </PosUiEmptyState>

      <template v-else>
        <PosV2TableHeader
          :table="table"
          :table-orders="tableOrders"
          :reservation="reservationHint"
          :search="search"
          :selected-count="selectedIds.size"
          :total-count="filteredOrders.length"
          :has-orders="tableOrders.length > 0"
          :can-create-order="canCreateOrder"
          :closing="closing"
          @update:search="search = $event"
          @select-all="selectAll"
          @clear-selection="clearSelection"
          @new-order="newOrder"
          @close-table="closeTable"
          @back="goBack"
        />

        <V2ReservationTableBanner
          v-if="table"
          :table-id="tableId"
          :table-orders="tableOrders"
          :can-create-order="canCreateOrder"
          @seated="createTableOrder"
        />

        <PosV2TableOrdersList
          :orders="listItems"
          :selected-ids="selectedIds"
          :searching="!!search.trim() && tableOrders.length > 0"
          @toggle-select="toggleSelect"
          @open-order="openOrder"
        />

        <PosV2TableFooter
          v-if="table"
          :table="table"
          :table-orders="tableOrders"
          :selected-orders="selectedOrders"
        />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-table-page {
    @apply tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-col tw-overflow-hidden tw-bg-canvas md:tw-flex-row;

    .page-nav {
      @apply tw-flex-none;
    }

    .page-main {
      @apply tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col;

      .not-found-state {
        @apply tw-flex-1;
      }
    }
  }
</style>
